import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../hooks/useTranslation';
import CustomSlider from '../CustomSlider';
import { EyeDropperIcon } from '@heroicons/react/24/outline';
import iro from '@jaames/iro';

/**
 * Controles de Pintura
 * Interface para configurar pincéis, cores e parâmetros de desenho
 */
const PaintingControls = ({
  currentTool,
  onToolChange,
  brushSize,
  onBrushSizeChange,
  brushColor,
  onBrushColorChange,
  brushOpacity,
  onBrushOpacityChange,
  brushFlow,
  onBrushFlowChange,
  brushSpacing,
  onBrushSpacingChange,
  brushAngle,
  onBrushAngleChange,
  brushRotate,
  onBrushRotateChange,
  stabilizerLevel,
  onStabilizerLevelChange,
  stabilizerWeight,
  onStabilizerWeightChange,
  onBrushImageSelect,
  currentBrushImage,
  blendMode,
  onBlendModeChange
}) => {
  const { t } = useTranslation();
  const [showColorModal, setShowColorModal] = useState(false);
  const [customColors, setCustomColors] = useState([]);
  const colorPickerContainerRef = useRef(null);
  const colorPickerRef = useRef(null);

  // Função para conta-gotas
  const handleEyedropper = async () => {
    if (!window.EyeDropper) {
      alert('O conta-gotas não é suportado neste navegador. Use um navegador moderno como Chrome, Edge ou Opera.');
      return;
    }

    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      if (result && result.sRGBHex) {
        onBrushColorChange(result.sRGBHex);
        // Adicionar cor customizada
        setCustomColors(prev => {
          if (!prev.includes(result.sRGBHex)) {
            return [...prev, result.sRGBHex];
          }
          return prev;
        });
        setShowColorModal(false);
      }
    } catch (err) {
      // Usuário cancelou a seleção
      if (err.name !== 'AbortError') {
        console.error('Erro ao usar conta-gotas:', err);
      }
    }
  };

  // Lista de pincéis disponíveis
  const brushes = [
    { name: 'Pixel', src: '/brush/pixel.webp', flow: 100, spacing: 20, rotate: false },
    { name: 'Círculo Fino', type: 'circle', flow: 100, spacing: 20, rotate: false },
    { name: 'Círculo Suave', type: 'circle', flow: 6, spacing: 2, rotate: false },
    { name: 'Textura 25%', src: '/brush/25.webp', flow: 100, spacing: 10, rotate: false },
    { name: 'Textura 50%', src: '/brush/50.webp', flow: 100, spacing: 10, rotate: false },
    { name: 'Textura 75%', src: '/brush/75.webp', flow: 100, spacing: 10, rotate: false },
    { name: 'Pincel 1', src: '/brush/b0.webp', flow: 30, spacing: 5, rotate: false },
    { name: 'Pincel 2', src: '/brush/b1.webp', flow: 35, spacing: 7, rotate: false },
    { name: 'Pincel 3', src: '/brush/b2.webp', flow: 50, spacing: 8, rotate: false },
    { name: 'Pincel 4', src: '/brush/b3.webp', flow: 20, spacing: 4, rotate: false },
    { name: 'Pincel 5', src: '/brush/b4.webp', flow: 30, spacing: 5, rotate: false },
    { name: 'Pincel 6', src: '/brush/b5.webp', flow: 100, spacing: 100, rotate: true },
    { name: 'Acrílico 1', src: '/brush/acrylic1.webp', flow: 50, spacing: 15, rotate: false },
    { name: 'Acrílico 2', src: '/brush/acrylic2.webp', flow: 50, spacing: 20, rotate: false },
    { name: 'Acrílico 3', src: '/brush/acrylic3.webp', flow: 40, spacing: 4, rotate: false },
    { name: 'Acrílico 4', src: '/brush/acrylic4.webp', flow: 70, spacing: 14, rotate: false },
    { name: 'Acrílico 5', src: '/brush/acrylic5.webp', flow: 40, spacing: 8, rotate: false },
    { name: 'Giz 1', src: '/brush/chalk1.webp', flow: 35, spacing: 7, rotate: false },
    { name: 'Giz 2', src: '/brush/chalk2.webp', flow: 35, spacing: 7, rotate: false },
    { name: 'Giz 3', src: '/brush/chalk3.webp', flow: 35, spacing: 7, rotate: false },
    { name: 'Carvão 1', src: '/brush/charcoal1.webp', flow: 40, spacing: 8, rotate: false },
    { name: 'Carvão 2', src: '/brush/charcoal2.webp', flow: 40, spacing: 8, rotate: false }
  ];

  const tools = [
    { id: 'pen', name: 'Pincel', icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' },
    { id: 'eraser', name: 'Apagar', icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' }
  ];

  const presetColors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
    '#FFFF00', '#FF00FF', '#00FFFF'
  ];

  // Combina cores predefinidas com cores customizadas
  const allColors = [...presetColors, ...customColors];

  // Inicializar iro.js quando modal abrir
  useEffect(() => {
    if (showColorModal && colorPickerContainerRef.current && !colorPickerRef.current) {
      const colorPicker = new iro.ColorPicker(colorPickerContainerRef.current, {
        width: 280,
        color: brushColor,
        layout: [
          {
            component: iro.ui.Box,
          },
          {
            component: iro.ui.Slider,
            options: {
              sliderType: 'hue'
            }
          }
        ]
      });

      colorPickerRef.current = colorPicker;

      // NÃO atualizar em tempo real para evitar re-renders

      return () => {
        if (colorPickerRef.current) {
          colorPickerRef.current = null;
        }
      };
    }
  }, [showColorModal]);

  return (
    <div className="p-4 space-y-4">
      {/* FERRAMENTAS */}
      <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Ferramenta</label>
            <div className="flex gap-1 bg-transparent p-1 rounded-lg">
              {tools.map(tool => (
                <button
                  key={tool.id}
                  onClick={() => onToolChange(tool.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md transition-all ${
                    currentTool === tool.id
                      ? 'bg-[#96c925] text-black shadow-lg'
                      : 'bg-transparent text-gray-400 hover:text-white'
                  }`}
                  title={tool.name}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tool.icon} />
                  </svg>
                  <span className="text-sm font-medium">{tool.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tamanho do Pincel */}
          <CustomSlider
            label={t('painting.size')}
            value={brushSize}
            min={1}
            max={200}
            step={0.1}
            onChange={onBrushSizeChange}
            unit="px"
            color="green"
            formatValue={(v) => Math.round(v)}
          />

          {/* Opacidade do Pincel */}
          <CustomSlider
            label={t('painting.opacity')}
            value={brushOpacity}
            min={0}
            max={100}
            step={1}
            onChange={onBrushOpacityChange}
            unit="%"
            color="green"
            formatValue={(v) => Math.round(v)}
          />

          {/* Intensidade (Fluxo) do Pincel */}
          <CustomSlider
            label="Intensidade"
            value={brushFlow}
            min={0}
            max={100}
            step={1}
            onChange={onBrushFlowChange}
            unit="%"
            color="green"
            formatValue={(v) => Math.round(v)}
          />

          {/* Seletor de Cor - apenas no modo caneta */}
          {currentTool !== 'eraser' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cor</label>
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="color"
                  value={brushColor}
                  onChange={(e) => onBrushColorChange(e.target.value)}
                  className="w-16 h-10 border-2 border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={brushColor}
                  onChange={(e) => onBrushColorChange(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="#000000"
                />
              </div>

              {/* Cores predefinidas + customizadas */}
              <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(10, minmax(0, 1fr))`, marginRight: '4px', marginBottom: '4px' }}>
                {allColors.map(color => (
                  <button
                    key={color}
                    onClick={() => onBrushColorChange(color)}
                    className={`w-5 h-5 rounded-full border-2 transition-all ${
                      brushColor.toUpperCase() === color.toUpperCase()
                        ? 'border-green-600 scale-110'
                        : 'border-gray-300 hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
                {/* Botão de cor customizada */}
                <button
                  onClick={() => setShowColorModal(true)}
                  className="w-5 h-5 rounded-full border-2 border-gray-300 hover:scale-105 transition-all flex items-center justify-center bg-white"
                  title="Adicionar cor customizada"
                >
                  <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Modo de Mesclagem - apenas no modo caneta */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-white mb-2">
                  {t('layers.blendMode')}
                </label>
                <select
                  value={blendMode || 'source-over'}
                  onChange={(e) => onBlendModeChange(e.target.value)}
                  className="w-full appearance-none bg-black/30 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 hover:bg-black/40 transition-colors"
                >
                  <option value="source-over" className="bg-gray-800">Normal</option>
                  <option value="multiply" className="bg-gray-800">Multiplicar</option>
                  <option value="screen" className="bg-gray-800">Tela</option>
                  <option value="overlay" className="bg-gray-800">Sobreposição</option>
                  <option value="darken" className="bg-gray-800">Escurecer</option>
                  <option value="lighten" className="bg-gray-800">Clarear</option>
                  <option value="color-dodge" className="bg-gray-800">Subexposição de Cor</option>
                  <option value="color-burn" className="bg-gray-800">Superexposição de Cor</option>
                  <option value="hard-light" className="bg-gray-800">Luz Forte</option>
                  <option value="soft-light" className="bg-gray-800">Luz Suave</option>
                  <option value="difference" className="bg-gray-800">Diferença</option>
                  <option value="exclusion" className="bg-gray-800">Exclusão</option>
                  <option value="hue" className="bg-gray-800">Matiz</option>
                  <option value="saturation" className="bg-gray-800">Saturação</option>
                  <option value="color" className="bg-gray-800">Cor</option>
                  <option value="luminosity" className="bg-gray-800">Luminosidade</option>
                </select>
              </div>
            </div>
          )}
      </div>

      {/* Divisor */}
      <div className="border-t border-gray-300"></div>

      {/* PINCÉIS */}
      <div>
          <label className="block text-sm font-medium text-white mb-2">Pincéis Disponíveis</label>
          <div className="grid grid-cols-3 gap-1">
            {brushes.map((brush, index) => (
              <button
                key={index}
                onClick={() => {
                  if (brush.src) {
                    onBrushImageSelect(brush.src);
                    onBrushFlowChange(brush.flow);
                    // onBrushSpacingChange(brush.spacing); // Mantido em 0
                    // onBrushRotateChange(brush.rotate); // Mantido desabilitado
                  } else if (brush.type === 'circle') {
                    // Resetar para pincel circular padrão
                    onBrushImageSelect(null);
                    onBrushFlowChange(brush.flow);
                    // onBrushSpacingChange(brush.spacing); // Mantido em 0
                    // onBrushRotateChange(brush.rotate); // Mantido desabilitado
                  }
                }}
                className={`p-2 border-2 rounded-lg transition-all hover:scale-105 bg-transparent ${
                  currentBrushImage === brush.src
                    ? 'border-white'
                    : 'border-transparent hover:border-white'
                }`}
                title={brush.name}
              >
                {brush.src ? (
                  <img src={brush.src} alt={brush.name} className="w-full h-12 object-contain brightness-0 invert" />
                ) : (
                  <div className="w-full h-12 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full bg-white"></div>
                  </div>
                )}
                <span className="block text-xs mt-1 text-center truncate text-white">{brush.name}</span>
              </button>
            ))}
          </div>
      </div>

      {/* Modal de Cor Customizada com iro.js - renderizado no body via Portal */}
      {showColorModal && createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50" onClick={() => setShowColorModal(false)}>
          <div className="bg-white rounded-lg p-6 relative shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Cor Customizada</h3>
              <button
                onClick={() => setShowColorModal(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Container para iro.js Color Picker */}
            <div ref={colorPickerContainerRef} className="flex justify-center items-center py-4" />
            
            {/* Botões Conta-gotas, OK/Cancelar */}
            <div className="flex space-x-3 mt-4">
              {/* Botão Conta-gotas */}
              <button
                onClick={handleEyedropper}
                className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                title="Conta-gotas"
              >
                <EyeDropperIcon className="w-5 h-5" />
              </button>
              
              {/* Botões OK/Cancelar */}
              <button
                onClick={() => setShowColorModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (colorPickerRef.current) {
                    const color = colorPickerRef.current.color.hexString;
                    onBrushColorChange(color);
                    // Adicionar cor customizada
                    setCustomColors(prev => {
                      if (!prev.includes(color)) {
                        return [...prev, color];
                      }
                      return prev;
                    });
                  }
                  setShowColorModal(false);
                }}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PaintingControls;

